import { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { Box } from '@twilio-paste/core/box';
import {
  Popover,
  PopoverContainer,
  PopoverBadgeButton,
  usePopoverState,
} from '@twilio-paste/core/popover';
import { Button } from '@twilio-paste/core/button';
import { Heading } from '@twilio-paste/core/heading';
import { Paragraph } from '@twilio-paste/core/paragraph';
import { CheckboxCheckIcon } from '@twilio-paste/icons/esm/CheckboxCheckIcon';
import { CloseIcon } from '@twilio-paste/icons/esm/CloseIcon';
import { SupportPlan } from '@twiliointernal/services-common/containers/SupportPlansView/definitions/types';

import { constants, copy, types } from '../definitions';
import { useCurrentPlan } from '../../../hooks';

import styles from '../layout.module.css';
import { routes } from '../../../definitions/constants';
import ErrorMenu from './errorMenu';
import { TopbarSupportPlanFeatures } from '../definitions/types';
import { dataTestIds } from '@twiliointernal/org/twilio/e2-commons/shared/definitions';

const SupportPlanMenuPopover = (): ReactElement => {
  const navigate = useNavigate();
  const { supportPlan, loading, hasError } = useCurrentPlan();
  const popover = usePopoverState({ baseId: 'supportPlansPopoverContainer' });

  if (!supportPlan || loading) {
    return <div />;
  }

  if (hasError) {
    return (
      <ErrorMenu
        type={types.ErrorMenuType.supportPlan}
        redirectUrl={routes.supportPlans}
      />
    );
  }

  const getFeaturesBySupportPlan = (
    supportPlan: SupportPlan,
  ): TopbarSupportPlanFeatures => {
    const features =
      constants.supportPlanFeatureByBillingItemSidMap[
        supportPlan.billableItemSid
      ] ?? // Default to the features for the free plan if billable item sid is not found in the map
      constants.topBarSupportPlanFeaturesDefault;

    return features;
  };

  const featuresBySupportPlan = getFeaturesBySupportPlan(supportPlan);

  return (
    <Box as="span" className={styles.topbarActionContainer}>
      <Box display="flex" columnGap="space80">
        <PopoverContainer state={popover}>
          <PopoverBadgeButton
            variant="decorative20"
            title="Open Support Plans view"
            data-testid={dataTestIds.supportPlansBadge}
          >
            {supportPlan.displayName}
          </PopoverBadgeButton>
          <Popover aria-label="Popover">
            <Box as="div" className={styles.topbarSupportPlansDetailsContainer}>
              <Heading variant="heading30" as="h3">
                {copy.topbar.supportPlan.detailsHeading(
                  supportPlan.displayName,
                )}
              </Heading>

              <Box as="div" className={styles.topbarMenuFeaturesContainer}>
                <Box
                  as="div"
                  className={styles.topbarMenuFeatureListItemContainer}
                >
                  <Paragraph marginBottom="space0">
                    {copy.topbar.supportPlan.planIncludes}
                  </Paragraph>

                  <Box as="span" className={styles.topbarMenuFeatureListItem}>
                    <CheckboxCheckIcon
                      decorative
                      color="colorTextIconSuccess"
                    />
                    {copy.topbar.supportPlan.webSupportLabel}
                  </Box>

                  {featuresBySupportPlan?.phone && (
                    <Box as="span" className={styles.topbarMenuFeatureListItem}>
                      <CheckboxCheckIcon
                        decorative
                        color="colorTextIconSuccess"
                      />
                      {copy.topbar.supportPlan.phoneSupportLabel}
                    </Box>
                  )}
                </Box>

                {!featuresBySupportPlan?.phone && (
                  <Box as="div">
                    <Paragraph marginBottom="space0">
                      {copy.topbar.supportPlan.planExcludes}
                    </Paragraph>

                    <Box as="span" className={styles.topbarMenuFeatureListItem}>
                      <CloseIcon decorative color="colorTextIconWarning" />
                      {copy.topbar.supportPlan.phoneSupportLabel}
                    </Box>
                  </Box>
                )}
              </Box>

              <Button
                variant="primary"
                onClick={(): void => {
                  navigate(routes.supportPlans);
                  popover.hide();
                }}
              >
                {copy.topbar.supportPlan.ctaValue}
              </Button>
            </Box>
          </Popover>
        </PopoverContainer>
      </Box>
    </Box>
  );
};

export default SupportPlanMenuPopover;
